// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2025 realvirtual GmbH <https://realvirtual.io>

/**
 * Render-mode registry — the single source of truth for the WebViewer's
 * rendering presets ("Unlit", "Shaded", "Toon").
 *
 * Note: the mode *ids* stay `'simple' | 'default' | 'toon'` (used as keys in
 * persisted settings, `modeSettings`, and preset files); only the user-facing
 * `label`s are "Unlit" / "Shaded" / "Toon".
 *
 * Each mode is described by a {@link RenderModeDescriptor} whose
 * {@link RenderModeCapabilities} flags drive BOTH:
 *  - the **UI** (which control blocks the Visual settings tab renders), and
 *  - the **pipeline** (which rendering features the viewer keeps active).
 *
 * Keeping these two concerns behind one descriptor means adding a new mode is a
 * single-entry change here — no scattered `mode === 'default'` branches.
 *
 * `'simple'` is intentionally minimal: a single flat AmbientLight, no
 * environment IBL, no tone mapping, no shadows, and no post-processing
 * (ambient occlusion / bloom). The composer is therefore never engaged
 * (see `PostProcessingManager.useComposer`), so Simple has zero unneeded
 * rendering work active. `'default'` enables the full feature set.
 */

export type RenderMode = 'simple' | 'default' | 'toon';

/**
 * Per-mode feature flags. Each flag gates one rendering feature in the
 * pipeline AND its corresponding control group in the Visual settings UI.
 *
 * Note: the global brightness control (`lightIntensity`) is shown in every
 * mode (its label switches to "Environment Intensity" when `environment` is
 * on), so it is deliberately NOT represented as a capability flag.
 */
export interface RenderModeCapabilities {
  /** Flat AmbientLight color + intensity (the "unlit" look used by Simple). */
  ambientLight: boolean;
  /** HDRI image-based lighting. Also relabels brightness as "Environment Intensity". */
  environment: boolean;
  /** Directional (key) light. */
  directionalLight: boolean;
  /** Filmic tone mapping (+ exposure). */
  toneMapping: boolean;
  /** Real-time shadow pass (implies {@link directionalLight}). */
  shadows: boolean;
  /** Ambient-occlusion post-processing (GTAO / N8AO). WebGL only. */
  ambientOcclusion: boolean;
  /** Bloom post-processing. WebGL only. */
  bloom: boolean;
  /** Floor mirror reflection. WebGL only. Off in toon (the floor is the flat
   *  cel-shaded checker, no reflection). Also gates the Reflection control in
   *  the Environment settings tab. */
  reflection: boolean;
  /**
   * Cel / toon shading. When set, the viewer swaps every scene material to a
   * banded {@link import('three').MeshToonMaterial} and draws a screen-space
   * Sobel outline (depth + normals). Also gates the toon-specific control block
   * in the Visual settings UI (band count, outline thickness/color, cool
   * shadows). WebGL only for the outline — on WebGPU the composer is inert so
   * toon renders as cel bands without lines.
   */
  toon: boolean;
}

/** A render-mode preset: identity, display metadata, and its capabilities. */
export interface RenderModeDescriptor {
  id: RenderMode;
  label: string;
  description: string;
  capabilities: RenderModeCapabilities;
}

/** Ordered list of all render modes (drives the Render Mode dropdown). */
export const RENDER_MODES: readonly RenderModeDescriptor[] = [
  {
    id: 'simple',
    label: 'Unlit',
    description: 'Unlit, minimal — fastest',
    capabilities: {
      ambientLight: true,
      environment: false,
      directionalLight: false,
      toneMapping: false,
      shadows: false,
      ambientOcclusion: false,
      bloom: false,
      reflection: true,
      toon: false,
    },
  },
  {
    id: 'default',
    label: 'Shaded',
    description: 'Fully rendered',
    capabilities: {
      ambientLight: false,
      environment: true,
      directionalLight: true,
      toneMapping: true,
      shadows: true,
      ambientOcclusion: true,
      bloom: true,
      reflection: true,
      toon: false,
    },
  },
  {
    id: 'toon',
    label: 'Toon',
    description: 'Cel-shaded, stylized',
    capabilities: {
      // Lightweight cel look: a flat ambient fill (brightness control) + a
      // single directional key light. A toon material bands the diffuse by
      // light direction and adds a hard specular highlight from the key light.
      // No HDRI environment (grey sky), no shadows, no tone mapping — just the
      // banded materials plus the screen-space Sobel edge (the `toon` flag).
      ambientLight: true,
      environment: false,
      directionalLight: true,
      toneMapping: false,
      shadows: false,
      ambientOcclusion: false,
      bloom: false,
      reflection: false,
      toon: true,
    },
  },
] as const;

/** Convenience list of mode ids (mirrors the legacy `LIGHTING_MODES` order). */
export const RENDER_MODE_IDS: readonly RenderMode[] = RENDER_MODES.map((m) => m.id);

/** Default mode used when a stored/looked-up id is missing or invalid. */
export const DEFAULT_RENDER_MODE: RenderMode = 'default';

/**
 * Look up a render-mode descriptor by id. Falls back to the
 * {@link DEFAULT_RENDER_MODE} descriptor for unknown ids so callers never
 * have to null-check.
 */
export function getRenderMode(id: RenderMode | string | undefined): RenderModeDescriptor {
  const found = RENDER_MODES.find((m) => m.id === id);
  return found ?? RENDER_MODES.find((m) => m.id === DEFAULT_RENDER_MODE)!;
}

/** True if the given mode supports a specific rendering capability. */
export function modeSupports(id: RenderMode | string | undefined, feature: keyof RenderModeCapabilities): boolean {
  return getRenderMode(id).capabilities[feature];
}

/** Type guard: is the value a known render-mode id? */
export function isRenderMode(value: unknown): value is RenderMode {
  return typeof value === 'string' && RENDER_MODES.some((m) => m.id === value);
}
